import {useMemo} from 'react'
import type {BaseColumnChartProps, ColumnChartProps} from '../types'
import {MarkColor} from '../types'
import {
  getDataWithDefaults,
  getTransformedPlotOptions,
  getXAxisOptionWithDefaults,
  getYAxisOptionsWithDefaults,
  isDev,
} from './shared'
import Chart from '../Chart'

const MAX_NUMBER_OF_SERIES_ITEMS = 5

export const ColumnDefaultTheme = {
  green: MarkColor.green,
  pine: MarkColor.pine,
  teal: MarkColor.teal,
  cyan: MarkColor.cyan,
  blue: MarkColor.blue,
  indigo: MarkColor.indigo,
  purple: MarkColor.purple,
  orange: MarkColor.orange,
}

export const BaseColumnChart = <T extends 'bar' | 'column'>({
  type,
  options,
  series: data,
  stacking,
  labels,
  colors,
  ...rest
}: BaseColumnChartProps<T>) => {
  const {xAxis, yAxis, plot} = options ?? {}
  const xyAxisOptionsTransformed = useMemo(() => getXAxisOptionWithDefaults({xAxis, gridLineWidth: 0}), [xAxis])
  const yAxisOptionsTransformed = useMemo(
    () => getYAxisOptionsWithDefaults({yAxis, gridLineDashStyle: 'Dash'}),
    [yAxis],
  )
  const series = useMemo(() => getDataWithDefaults(data as Highcharts.SeriesOptionsType[], type), [data, type])
  const plotOptionsTransformed = useMemo(
    () => getTransformedPlotOptions({plotOptions: plot, labels, stacking}),
    [labels, stacking, plot],
  )

  if (series.length > MAX_NUMBER_OF_SERIES_ITEMS && isDev()) {
    // eslint-disable-next-line no-console
    console.error('Ideally, a column chart should contain no more than 5 segments or columns.')
  }

  return (
    <Chart
      type={type}
      xAxisOptions={xyAxisOptionsTransformed}
      yAxisOptions={yAxisOptionsTransformed}
      series={series as Highcharts.SeriesOptionsType[]}
      plotOptions={plotOptionsTransformed}
      colors={
        colors
          ? colors.map(color => `var(${MarkColor[color]})`)
          : Object.keys(ColumnDefaultTheme).map(
              key => `var(${ColumnDefaultTheme[key as keyof typeof ColumnDefaultTheme]})`,
            )
      }
      {...rest}
    />
  )
}

const ColumnChart = (props: ColumnChartProps) => {
  return <BaseColumnChart {...props} type="column" />
}

export default ColumnChart

try{ BaseColumnChart.displayName ||= 'BaseColumnChart' } catch {}
try{ ColumnChart.displayName ||= 'ColumnChart' } catch {}